// Dependencies
import React from 'react';

import { Link, Text } from '@nerdwallet/currency';

import { EVENT_TYPE, SECTION_NAME } from '../../lib/analytics-constants';

import inputLibrary from './inputs';

export default [
  {
    label: {
      header: <Text>How’s your credit score?</Text>,
      content: (
        <Text small>
          Not sure? Check your{' '}
          <Link
            target="_blank"
            href="https://www.nerdwallet.com/home/dashboard/credit-score"
          >
            free credit score
          </Link>
        </Text>
      ),
    },
    input: inputLibrary.creditScore.getViewConfig({
      index: 0,
      value: '{{0}}',
      keys: [
        {
          key: 'inputs.creditScore',
        },
      ],
      analytics: {
        eventType: EVENT_TYPE.HOME_CREDIT_SCORE_BAND_UPDATED,
        payload: {
          form_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
        },
        valueFieldName: 'value_s',
        mapValue: (value) => {
          switch (value) {
            case 'excellent':
              return 'EXCELLENT_720_850';
            case 'good':
              return 'GOOD_690_719';
            case 'average':
              return 'AVERAGE_630_689';
            case 'belowAvg':
              return 'BELOW_AVERAGE_580_629';
            case 'poor':
              return 'POOR_350_579';
            default:
              return null;
          }
        },
      },
      additionalInfo: {
        header: <Text>Why do we ask for this?</Text>,
        content: (
          <Text small>
            We ask you several questions to match you with the lowest interest
            rate financing possible. We’re asking your credit score because some
            financing types have minimum credit score requirements. For NW
            members, we’ve pre-populated your credit score.
          </Text>
        ),
      },
    }),
  },
  {
    label: {
      header: <Text>How much will your home improvement cost?</Text>,
      content: (
        <Text small>
          If you’re not sure, use{' '}
          <Link
            target="_blank"
            href="/blog/mortgages/estimate-the-cost-of-your-home-renovation/"
          >
            NerdWallet’s Home Renovation Cost Estimator
          </Link>
        </Text>
      ),
    },
    input: inputLibrary.homeImprovementCost.getViewConfig({
      index: 1,
      value: '{{0}}',
      keys: [
        {
          key: 'inputs.homeImprovementCost',
        },
      ],
      analytics: {
        eventType: EVENT_TYPE.HOME_RENOVATION_COST_UPDATED,
        payload: {
          form_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
        },
        valueFieldName: 'value_i',
      },
      additionalInfo: {
        header: <Text>Why do we ask for this?</Text>,
        content: (
          <Text small>
            We’re asking how much financing you need to help determine how much
            of your home’s equity you can use, if any. Even if you can’t use
            your home’s equity, we still need to know to help determine whether
            a credit card or personal loan is a better option for you.
          </Text>
        ),
      },
    }),
  },
  {
    label: {
      header: (
        <Text>What is the outstanding amount on your current mortgage?</Text>
      ),
      content: <Text small>If you have no mortgage, enter 0</Text>,
    },
    input: inputLibrary.mortgageAmount.getViewConfig({
      index: 2,
      value: '{{0}}',
      keys: [
        {
          key: 'inputs.mortgageAmount',
        },
      ],
      analytics: {
        eventType: EVENT_TYPE.MORTGAGE_BALANCE_UPDATED,
        payload: {
          form_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
        },
        valueFieldName: 'value_i',
      },
      additionalInfo: {
        header: <Text>Why do we ask for this?</Text>,
        content: (
          <Text small>
            We’re asking for your mortgage balance to help determine whether you
            can use your home’s equity, which will help to secure a lower
            interest rate compared to other options, such as a personal loan.
            For NW members, we’ve pre-populated your current mortgage amount
            based on your credit report.
          </Text>
        ),
      },
    }),
  },
  {
    label: {
      header: <Text>What is the estimated value of your home?</Text>,
      content: (
        <Text small>
          If you’re not sure, use{' '}
          <Link target="_blank" href="/mortgages/home-value">
            NerdWallet’s Home Value Estimator
          </Link>
          ; if you do not own the property you live in, enter 0
        </Text>
      ),
    },
    input: inputLibrary.homeValue.getViewConfig({
      index: 3,
      value: '{{0}}',
      callback: ({
        homeImprovementCost = 0,
        mortgageAmount = 0,
        homeValue = 0,
      }) => {
        if (mortgageAmount === 0) {
          return { question: 'inputs.revolvingLineOfCredit' };
        }

        const equityLTV = (homeImprovementCost + mortgageAmount) / homeValue;
        if (equityLTV <= 0.8) {
          return { question: 'inputs.isRateLower' };
        }
        return { question: 'inputs.loanPayOffTime' };
      },
      keys: [
        {
          key: 'inputs.homeValue',
        },
      ],
      analytics: {
        eventType: EVENT_TYPE.HOME_VALUE_UPDATED,
        payload: {
          form_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
        },
        valueFieldName: 'value_i',
      },
      additionalInfo: {
        header: <Text>Why do we ask for this?</Text>,
        content: (
          <Text small>
            We’re asking for the estimated value of your home to help determine
            how much of your home’s equity you can use, if any. This could help
            you to secure a lower interest rate.
          </Text>
        ),
      },
    }),
  },
  {
    label: {
      header:
        'Is your existing mortgage rate lower than current mortgage interest rates, approximately {{0}}% for 30 year fixed rates?',
      keys: [
        {
          key: 'rate',
        },
      ],
    },
    input: inputLibrary.isRateLower.getViewConfig({
      index: 4,
      value: '{{0}}',
      keys: [
        {
          key: 'inputs.isRateLower',
        },
      ],
      analytics: {
        eventType: EVENT_TYPE.MORTGAGE_RATE_COMPARE_UPDATED,
        payload: {
          form_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
        },
        valueFieldName: 'value_s',
        mapValue: (value) => {
          switch (value) {
            case 'yes':
              return 'YES_LOWER_THAN_CURRENT';
            case 'no':
              return 'NO_HIGHER_THAN_CURRENT';
            default:
              return null;
          }
        },
      },
      additionalInfo: {
        header: <Text>Why do we ask for this?</Text>,
        content: (
          <Text small>
            We’re asking for the interest rate on your existing mortgage to help
            determine whether a refinance or home equity product is best for
            you. A refinance can come with a lower interest rate but isn’t right
            for everyone.
          </Text>
        ),
      },
    }),
  },
  {
    label: {
      header: (
        <Text>Do you value the flexibility of a revolving line of credit?</Text>
      ),
    },
    input: inputLibrary.revolvingLineOfCredit.getViewConfig({
      index: 5,
      value: '{{0}}',
      keys: [
        {
          key: 'inputs.revolvingLineOfCredit',
        },
      ],
      analytics: {
        eventType: EVENT_TYPE.CREDIT_FLEXIBILITY_UPDATED,
        payload: {
          form_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
        },
        valueFieldName: 'value_s',
        mapValue: (value) => {
          switch (value) {
            case 'yes':
              return 'YES';
            case 'no':
              return 'NO';
            default:
              return null;
          }
        },
      },
      additionalInfo: {
        header: <Text>Why do we ask for this?</Text>,
        content: (
          <Text small>
            We’re helping you to determine whether a home equity loan or home
            equity line of credit is your best for you. If you want the full
            amount at once, a loan is usually a better option. If you’re
            planning to improve your home in phases, a line of credit is usually
            a better option.
          </Text>
        ),
      },
    }),
  },
  {
    label: {
      header: (
        <Text>How long will you need to pay off your new financing?</Text>
      ),
    },
    input: inputLibrary.loanPayOffTime.getViewConfig({
      index: 6,
      value: '{{0}}',
      callback: ({
        homeImprovementCost = 0,
        creditScore = 'average',
        loanPayOffTime = 21,
      }) => {
        if (
          homeImprovementCost < 15000 &&
          ['good', 'excellent'].includes(creditScore) &&
          loanPayOffTime <= 21
        ) {
          return { result: 'creditCard' };
        }
        return { result: 'personalLoan' };
      },
      keys: [
        {
          key: 'inputs.loanPayOffTime',
        },
      ],
      analytics: {
        eventType: EVENT_TYPE.HOME_RENOVATION_FINANCING_TERM_UPDATED,
        payload: {
          form_name: SECTION_NAME.MRT_HOME_IMPROVEMENT_LOAN,
        },
        valueFieldName: 'value_s',
        mapValue: (value) => {
          switch (value) {
            case '48':
              return 'AT_LEAST_48_MONTHS';
            case '47':
              return '22_TO_47_MONTHS';
            case '21':
              return '12_TO_21_MONTHS';
            case '12':
              return 'LESS_THAN_12_MONTHS';
            default:
              return null;
          }
        },
      },
      additionalInfo: {
        header: <Text>Why do we ask for this?</Text>,
        content: (
          <Text small>
            We’re helping you to determine whether a personal loan or credit
            card is best for you. Credit cards can offer low interest rates for
            introductory periods, but aren’t right for everyone.
          </Text>
        ),
      },
    }),
  },
];
